import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  fetchProjects, deleteProject, downloadVideoFile, ensureShareToken, updateProjectName, getFreshVideoUrl,
  fetchBrandProfiles, createBrandProfile, updateBrandProfile, deleteBrandProfile, uploadBrandProfileLogo,
  supabase,
} from '../services/supabase';
import type { ImagemotionProject } from '../types';
import { QUALITY_TIERS } from '../types';
import { MediaBrandManager } from '@rehub/branded-viewer';
import type { MediaBrandProfile, MediaBrandProfileInput } from '@rehub/branded-viewer';
import { Film, Plus, Clock, CheckCircle2, AlertCircle, Loader2, Trash2, Download, Share2, Copy, Check, Mail, Shield, Timer, Pencil, Sparkles, Palette, ExternalLink, ArrowRight, Play, ChevronLeft, ChevronRight } from 'lucide-react';

type AppView = 'LOADING' | 'LOGIN' | 'DASHBOARD' | 'NEW_PROJECT' | 'PROJECT_DETAIL' | 'STAGING_ANIMATION';
const HYPER_DUSK_APP_URL = 'https://hyperdusk.rehub.studio';
const PROPERTYCUT_APP_URL = 'https://propertycuts.rehub.studio';
const VIDEOMOTION_APP_URL = 'https://videomotion.rehub.studio';
const PHOTOCLEAR_APP_URL = 'https://photoclear.rehub.studio';

const TOOLBAR_BTN =
  'inline-flex h-9 min-w-[9rem] items-center justify-center gap-1.5 px-3 rounded-lg text-sm font-medium transition-colors border shrink-0';

function SiblingAppChip({
  href,
  title,
  src,
  alt,
  accent,
  imgClass,
}: {
  href: string;
  title: string;
  src: string;
  alt: string;
  accent: string;
  imgClass: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      className={`${TOOLBAR_BTN} overflow-hidden bg-slate-800 text-white hover:bg-slate-700 ${accent}`}
    >
      <span className="flex h-8 w-[6.25rem] items-center justify-center">
        <img src={src} alt={alt} className={`w-auto object-contain ${imgClass}`} />
      </span>
      <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-80" />
    </a>
  );
}

/**
 * Open PropertyCuts with a deep-link to import this completed video as a
 * fresh draft project. Uses the same SSO-via-hash pattern as the rest of
 * the ReHub product switcher / cross-app handoffs (see e.g.
 * `apps/staging/components/Dashboard.tsx::handleSendToHyperdusk`).
 *
 * If the popup-blocker eats `window.open`, we fall back to navigating the
 * current tab as a last resort so the flow still completes.
 */
async function openPropertyCutWithProject(project: ImagemotionProject): Promise<void> {
  const params = new URLSearchParams({
    source: 'imagemotion',
    projectId: project.id,
  });
  let hash = '';
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      hash =
        `#sso_access_token=${encodeURIComponent(session.access_token)}` +
        `&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
    }
  } catch {
    // Fall through; PropertyCuts will prompt for login if the session
    // can't be forwarded.
  }
  const url = `${PROPERTYCUT_APP_URL}/?${params.toString()}${hash}`;
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win || win.closed) {
    window.location.href = url;
  }
}

/** Resize public Supabase storage images for dashboard cards. Signed URLs stay intact. */
function dashboardImageUrl(imageUrl: string | null | undefined, width = 640): string | null {
  if (!imageUrl) return null;
  if (!imageUrl.includes('/storage/v1/object/') || imageUrl.includes('/object/sign/')) return imageUrl;
  const [base, query = ''] = imageUrl.split('?');
  const params = new URLSearchParams(query);
  params.set('width', String(width));
  params.set('quality', '60');
  params.set('resize', 'cover');
  return `${base.replace('/storage/v1/object/', '/storage/v1/render/image/')}?${params.toString()}`;
}

const IMAGEMOTION_CARD_IMAGE = '/create-video-card.jpg';
const STAGING_CARD_EMPTY = '/staging-card-empty.jpg';
const STAGING_CARD_STAGED = '/staging-card-staged.jpg';

function CreateVideoCard({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden flex flex-col">
      <div className="h-44 relative bg-slate-950">
        <img
          src={IMAGEMOTION_CARD_IMAGE}
          alt="ImageMotion property video"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-950/25" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-lg">
            <Play className="h-5 w-5 ml-0.5" fill="currentColor" />
          </span>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="sr-only">Create Video</h2>
            <img
              src="/Logo.webp"
              alt=""
              className="block h-[1.6rem] w-auto max-w-none object-contain"
            />
          </div>
          <p className="text-sm text-slate-400">Turn property photos into a walkthrough video.</p>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          New Project
        </button>
      </div>
    </div>
  );
}

function StagingToolCard({ onOpen }: { onOpen: () => void }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden flex flex-col">
      <div className="h-44 relative shrink-0 bg-slate-950">
        <div className="absolute inset-0 flex">
          <div className="relative w-1/2 overflow-hidden bg-slate-800">
            <img src={STAGING_CARD_EMPTY} alt="Empty room" className="h-full w-full object-cover object-[center_42%]" />
            <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/75 to-transparent" />
            <span className="absolute bottom-2 left-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
              Empty
            </span>
          </div>
          <div className="relative w-1/2 overflow-hidden bg-amber-950">
            <img src={STAGING_CARD_STAGED} alt="Staged room" className="h-full w-full object-cover object-[center_42%]" />
            <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/75 to-transparent" />
            <span className="absolute bottom-2 right-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white">
              Staged
            </span>
          </div>
        </div>
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center pointer-events-none">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-900 shadow-lg ring-2 ring-black/20">
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="sr-only">Staging Animation</h2>
            <img
              src="/staging-animation-logo.png"
              alt=""
              width={817}
              height={122}
              className="h-8 w-auto"
            />
            <span className="text-[10px] font-bold uppercase tracking-wide bg-blue-600 text-white px-1.5 py-0.5 rounded">New</span>
          </div>
          <p className="text-sm text-slate-400">Turn an empty room into a fully staged animated transformation.</p>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gradient-to-r from-[#a363ff] to-[#fe917a] text-white font-medium hover:from-[#b47aff] hover:to-[#ffaa96] shadow-lg shadow-purple-900/30"
        >
          Create Animation
        </button>
      </div>
    </div>
  );
}

type StagingAnimationCard = {
  id: string;
  project_name: string | null;
  status: string;
  created_at: string;
  thumbnail_url: string | null;
  before_image_url: string | null;
  after_image_url: string | null;
  after_storage_path: string | null;
  output_storage_path: string | null;
  source: string;
  output_url: string | null;
};

type DashboardProject = ImagemotionProject & { image_count: number; thumbnail_url: string | null };

type DashboardItem =
  | { kind: 'animation'; row: StagingAnimationCard }
  | { kind: 'project'; row: DashboardProject };

type DashboardFilter = 'all' | 'imagemotion' | 'staging';

const PAGE_SIZE = 12;

const FILTER_OPTIONS: { id: DashboardFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'imagemotion', label: 'ImageMotion' },
  { id: 'staging', label: 'Staging Animation' },
];

const statusConfig: Record<
  string,
  { label: string; color: string; icon: typeof Clock }
> = {
  queued: {
    label: 'Queued',
    color: 'bg-slate-800 text-slate-300',
    icon: Clock,
  },
  generating_clips: {
    label: 'Generating',
    color: 'bg-amber-900/30 text-amber-400',
    icon: Loader2,
  },
  editing: {
    label: 'Ready to review',
    color: 'bg-blue-900/30 text-blue-400',
    icon: Pencil,
  },
  rendering: {
    label: 'Rendering',
    color: 'bg-indigo-900/30 text-indigo-400',
    icon: Loader2,
  },
  completed: {
    label: 'Completed',
    color: 'bg-green-900/30 text-green-400',
    icon: CheckCircle2,
  },
  failed: {
    label: 'Failed',
    color: 'bg-red-900/30 text-red-400',
    icon: AlertCircle,
  },
  expired: {
    label: 'Expired',
    color: 'bg-slate-800 text-slate-500',
    icon: Clock,
  },
};

interface DashboardProps {
  userId: string;
  onNavigate: (view: AppView, projectId?: string) => void;
  onOpenStagingAnimation: (jobId?: string) => void;
}

export default function Dashboard({ userId, onNavigate, onOpenStagingAnimation }: DashboardProps) {
  const [projects, setProjects] = useState<
    (ImagemotionProject & { image_count: number; thumbnail_url: string | null })[]
  >([]);
  const [animations, setAnimations] = useState<StagingAnimationCard[]>([]);
  const [animationThumbs, setAnimationThumbs] = useState<Record<string, string | null>>({});
  const [typeFilter, setTypeFilter] = useState<DashboardFilter>('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [shareMenuId, setShareMenuId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [generatingTokenId, setGeneratingTokenId] = useState<string | null>(null);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Brand manager state
  const [showBrandManager, setShowBrandManager] = useState(false);
  const [brands, setBrands] = useState<MediaBrandProfile[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);

  const loadBrands = useCallback(async () => {
    setBrandsLoading(true);
    const data = await fetchBrandProfiles(userId);
    setBrands(data);
    setBrandsLoading(false);
    return data;
  }, [userId]);

  useEffect(() => {
    if (showBrandManager && brands.length === 0) loadBrands();
  }, [showBrandManager]);

  const getShareUrl = (shareToken: string) => `${window.location.origin}/view/${shareToken}`;

  const resolveShareToken = async (project: ImagemotionProject): Promise<string | null> => {
    if (project.share_token) return project.share_token;
    setGeneratingTokenId(project.id);
    try {
      const token = await ensureShareToken(project.id);
      if (token) {
        setProjects((prev) =>
          prev.map((p) => (p.id === project.id ? { ...p, share_token: token } : p))
        );
      }
      return token;
    } finally {
      setGeneratingTokenId(null);
    }
  };

  const copyToClipboard = (text: string, projectId: string) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
    setCopiedId(projectId);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const fallbackCopy = (text: string) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };

  const handleCopyShareLink = async (project: ImagemotionProject) => {
    const token = await resolveShareToken(project);
    if (!token) return;
    copyToClipboard(getShareUrl(token), project.id);
  };

  const startEditingName = (e: React.MouseEvent, project: ImagemotionProject & { image_count: number; thumbnail_url: string | null }) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingNameId(project.id);
    setEditingNameValue(project.name || '');
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const saveEditingName = async () => {
    const id = editingNameId;
    if (!id) return;
    setEditingNameId(null);
    const trimmed = editingNameValue.trim();
    try {
      await updateProjectName(id, trimmed);
      setProjects((prev) =>
        prev.map((p) => (p.id === id ? { ...p, name: trimmed || null } : p))
      );
    } catch (err) {
      console.error('Failed to update project name:', err);
    }
  };

  const handleSocialShare = async (project: ImagemotionProject, platform: string) => {
    const token = await resolveShareToken(project);
    if (!token) return;
    const url = getShareUrl(token);
    const title = project.name
      ? `Check out "${project.name}" — created with ImageMotion`
      : 'Check out this video — created with ImageMotion';

    const urls: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + '\n' + url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(title + '\n\n' + url)}`,
    };

    if (platform === 'email') {
      window.open(urls[platform], '_self');
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    setShareMenuId(null);
  };

  // Close share menu on outside click
  useEffect(() => {
    if (!shareMenuId) return;
    const handleClick = () => setShareMenuId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [shareMenuId]);

  const handleDownload = async (
    url: string,
    filename: string,
    projectId: string,
    storagePath?: string | null
  ) => {
    setDownloadingId(projectId);
    try {
      await downloadVideoFile(url, filename, { projectId, storagePath });
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    async function load() {
      const [data, animationRows] = await Promise.all([
        fetchProjects(userId),
        supabase
          .from('transition_animations')
          .select('id, project_name, status, created_at, thumbnail_url, before_image_url, after_image_url, after_storage_path, output_storage_path, source, output_url')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
      ]);
      setProjects(data);
      setAnimations((animationRows.data ?? []) as StagingAnimationCard[]);
      setLoading(false);
    }
    load();
  }, [userId]);

  const items: DashboardItem[] = [
    ...(typeFilter === 'imagemotion' ? [] : animations.map((row) => ({ kind: 'animation' as const, row }))),
    ...(typeFilter === 'staging' ? [] : projects.map((row) => ({ kind: 'project' as const, row }))),
  ].sort((a, b) => new Date(b.row.created_at).getTime() - new Date(a.row.created_at).getTime());

  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const pageAnimationKey = pageItems
    .filter((item) => item.kind === 'animation')
    .map((item) => item.row.id)
    .join(',');

  useEffect(() => {
    const pending = pageItems.flatMap((item) =>
      item.kind === 'animation' && !(item.row.id in animationThumbs) ? [item.row] : []
    );
    if (pending.length === 0) return;
    let cancelled = false;
    Promise.all(
      pending.map(async (row) => {
        const staged = await getFreshVideoUrl(row.after_storage_path, row.after_image_url);
        return [row.id, staged || row.after_image_url || row.thumbnail_url] as const;
      })
    ).then((entries) => {
      if (cancelled) return;
      setAnimationThumbs((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => { cancelled = true; };
  }, [pageAnimationKey]);

  const changeFilter = (next: DashboardFilter) => {
    setTypeFilter(next);
    setPage(1);
  };

  const changePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteAnimation = async (animation: StagingAnimationCard) => {
    setDeletingId(animation.id);
    try {
      const paths = [animation.output_storage_path].filter((path): path is string => Boolean(path));
      if (paths.length > 0) {
        await supabase.storage.from('imagemotion-uploads').remove(paths);
      }
      const { error } = await supabase.from('transition_animations').delete().eq('id', animation.id);
      if (error) throw error;
      setAnimations((prev) => prev.filter((row) => row.id !== animation.id));
    } catch (err) {
      console.error('Failed to delete staging animation:', err);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleDelete = async (projectId: string) => {
    setDeletingId(projectId);
    try {
      await deleteProject(projectId);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
    } catch (err) {
      console.error('Failed to delete project:', err);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (projects.length === 0 && animations.length === 0) {
    return (
      <div className="text-center py-20 animate-fade-in">
        <Film className="w-16 h-16 mx-auto text-slate-700 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">No projects yet</h2>
        <p className="text-slate-400 mb-6">
          Create your first video from images
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto text-left">
          <CreateVideoCard onOpen={() => onNavigate('NEW_PROJECT')} />
          <StagingToolCard onOpen={() => onOpenStagingAnimation()} />
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Projects</h1>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button
            onClick={() => setShowBrandManager(true)}
            className={`${TOOLBAR_BTN} bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border-slate-700`}
          >
            <Palette className="w-4 h-4" />
            Brands
          </button>
          <SiblingAppChip
            href={HYPER_DUSK_APP_URL}
            title="Open Hyper Dusk"
            src="/hyperdusk-logo-chip.png"
            alt="Hyper Dusk"
            accent="border-indigo-500/40 hover:border-indigo-400"
            imgClass="h-[11px] max-w-[5.25rem]"
          />
          <SiblingAppChip
            href={PROPERTYCUT_APP_URL}
            title="Open PropertyCuts"
            src="/propertycut-logo-chip.png"
            alt="PropertyCuts"
            accent="border-teal-500/40 hover:border-teal-400"
            imgClass="h-8 max-w-full"
          />
          <SiblingAppChip
            href={VIDEOMOTION_APP_URL}
            title="Open Videomotion"
            src="/videomotion-logo-chip.png"
            alt="Videomotion"
            accent="border-orange-500/40 hover:border-orange-400"
            imgClass="h-[22px] max-w-full"
          />
          <SiblingAppChip
            href={PHOTOCLEAR_APP_URL}
            title="Open Photoclear"
            src="/photoclear-logo-chip.png"
            alt="Photoclear"
            accent="border-sky-500/40 hover:border-sky-400"
            imgClass="h-[22px] max-w-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <CreateVideoCard onOpen={() => onNavigate('NEW_PROJECT')} />
        <StagingToolCard onOpen={() => onOpenStagingAnimation()} />
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-800 bg-slate-900 p-1">
          {FILTER_OPTIONS.map((option) => {
            const count =
              option.id === 'all'
                ? projects.length + animations.length
                : option.id === 'imagemotion'
                  ? projects.length
                  : animations.length;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => changeFilter(option.id)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  typeFilter === option.id
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                {option.label}
                <span className={`ml-1.5 text-xs ${typeFilter === option.id ? 'text-blue-100' : 'text-slate-500'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {items.length > 0 && (
          <span className="text-sm text-slate-500">
            Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, items.length)} of {items.length}
          </span>
        )}
      </div>

      {items.length === 0 && (
        <div className="text-center py-16 border border-dashed border-slate-800 rounded-lg">
          <Film className="w-10 h-10 mx-auto text-slate-700 mb-3" />
          <p className="text-sm text-slate-400">
            {typeFilter === 'staging' ? 'No staging animations yet' : 'No ImageMotion videos yet'}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pageItems.map((item) => {
          if (item.kind === 'animation') {
          const animation = { ...item.row, thumbnail_url: animationThumbs[item.row.id] ?? null };
          const isDeleting = deletingId === animation.id;
          const isConfirming = confirmDeleteId === animation.id;
          return (
          <div
            key={animation.id}
            className="relative text-left bg-slate-900 rounded-lg border border-slate-800 overflow-hidden hover:border-slate-700"
          >
            {isConfirming && (
              <div className="absolute inset-0 z-10 bg-slate-900/95 rounded-lg flex flex-col items-center justify-center gap-3 p-4">
                <p className="text-sm text-slate-300 text-center">Delete this animation?</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handleDeleteAnimation(animation)}
                    disabled={isDeleting}
                    className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Delete'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteId(null)}
                    className="px-3 py-1.5 bg-slate-700 text-slate-300 text-xs font-medium rounded-md hover:bg-slate-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => onOpenStagingAnimation(animation.id)}
              className="block w-full text-left"
            >
            <div className="aspect-video bg-slate-800 relative">
              {animation.thumbnail_url ? (
                <img
                  src={dashboardImageUrl(animation.thumbnail_url, 480) ?? animation.thumbnail_url}
                  alt=""
                  width={480}
                  height={270}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Film className="w-10 h-10 text-slate-700" />
                </div>
              )}
              <span className="absolute top-2 left-2 text-[10px] font-semibold uppercase tracking-wide bg-blue-600 text-white px-2 py-0.5 rounded-full">
                Staging Animation
              </span>
            </div>
            <div className="p-4">
              <p className="text-sm font-medium text-white">{animation.project_name || 'Staging Animation'}</p>
              <p className="text-xs text-slate-400 mt-1 capitalize">{animation.status.replace('_', ' ')}</p>
              {animation.source === 'virtual_staging' && (
                <p className="text-[11px] text-indigo-300 mt-1">Created from Virtual Staging</p>
              )}
            </div>
            </button>
            <button
              type="button"
              onClick={() => setConfirmDeleteId(animation.id)}
              className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-red-400 rounded-md bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
              title="Delete animation"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          );
          }
          const project = item.row;
          const config = statusConfig[project.status] ?? statusConfig.queued;
          const Icon = config.icon;
          const isAnimated = ['generating_clips', 'rendering'].includes(
            project.status
          );

          const isDeleting = deletingId === project.id;
          const isConfirming = confirmDeleteId === project.id;

          return (
            <div
              key={project.id}
              className="relative bg-slate-900 rounded-lg border border-slate-800 hover:border-slate-700 transition-all hover:shadow-lg hover:shadow-slate-900/50 overflow-hidden"
            >
              {isConfirming && (
                <div className="absolute inset-0 z-10 bg-slate-900/95 rounded-lg flex flex-col items-center justify-center gap-3 p-4">
                  <p className="text-sm text-slate-300 text-center">Delete this project?</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDelete(project.id)}
                      disabled={isDeleting}
                      className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {isDeleting ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        'Delete'
                      )}
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="px-3 py-1.5 bg-slate-700 text-slate-300 text-xs font-medium rounded-md hover:bg-slate-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Thumbnail only — name editing must not live inside this button */}
              <button
                type="button"
                onClick={() => onNavigate('PROJECT_DETAIL', project.id)}
                className="block text-left w-full"
              >
                <div className="aspect-video bg-slate-800 relative">
                  {project.thumbnail_url ? (
                    <img
                      src={dashboardImageUrl(project.thumbnail_url, 480) ?? project.thumbnail_url}
                      alt={project.name || 'Project thumbnail'}
                      width={480}
                      height={270}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-10 h-10 text-slate-700" />
                    </div>
                  )}

                  {/* Status badge overlaid on thumbnail */}
                  <span
                    className={`absolute top-2 left-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${config.color}`}
                  >
                    <Icon
                      className={`w-3.5 h-3.5 ${isAnimated ? 'animate-spin' : ''}`}
                    />
                    {config.label}
                  </span>

                  <span className="absolute bottom-2 left-2 text-xs text-slate-300 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
                    {new Date(project.created_at).toLocaleDateString()}
                  </span>

                  {/* Promoted / Expiring badge */}
                  {project.status === 'completed' && project.is_promoted && (
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-600/80 text-white text-[10px] font-medium rounded backdrop-blur-sm">
                      <Shield size={10} /> Active
                    </span>
                  )}
                  {project.status === 'completed' && !project.is_promoted && (() => {
                    const days = Math.max(0, 30 - Math.floor((Date.now() - new Date(project.created_at).getTime()) / 86400000));
                    return days <= 7 ? (
                      <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-600/80 text-white text-[10px] font-medium rounded backdrop-blur-sm">
                        <Timer size={10} /> {days}d left
                      </span>
                    ) : null;
                  })()}
                </div>
              </button>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center gap-1.5">
                  {editingNameId === project.id ? (
                    <input
                      ref={nameInputRef}
                      value={editingNameValue}
                      onChange={(e) => setEditingNameValue(e.target.value)}
                      onBlur={saveEditingName}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveEditingName();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setEditingNameId(null);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="flex-1 min-w-0 text-sm font-medium text-white bg-slate-800 border border-slate-600 rounded px-1.5 py-0.5 outline-none focus:border-blue-500 transition-colors"
                      placeholder="Project name"
                    />
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => onNavigate('PROJECT_DETAIL', project.id)}
                        className="text-sm font-medium text-white truncate flex-1 min-w-0 text-left"
                      >
                        {project.name || 'Untitled Project'}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => startEditingName(e, project)}
                        className="shrink-0 p-0.5 text-slate-500 hover:text-slate-300 transition-colors"
                        title="Rename project"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate('PROJECT_DETAIL', project.id)}
                  className="flex items-center gap-2 mt-1 w-full text-left"
                >
                  <span className="text-xs text-slate-500">
                    {project.image_count} image{project.image_count !== 1 ? 's' : ''}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      project.quality === 'ultra_premium' || project.quality === 'premium'
                        ? 'bg-purple-500/15 text-purple-400'
                        : project.quality === 'standard'
                          ? 'bg-amber-500/15 text-amber-400'
                          : project.quality === 'tester'
                            ? 'bg-emerald-500/15 text-emerald-400'
                            : 'bg-slate-700/50 text-slate-400'
                    }`}
                  >
                    {(project.quality === 'premium' || project.quality === 'ultra_premium' || project.quality === 'standard') && <Sparkles className="w-2.5 h-2.5" />}
                    {QUALITY_TIERS[project.quality as keyof typeof QUALITY_TIERS]?.label ?? project.quality}
                  </span>
                  {project.service_type === 'hyper_dusk' && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-500/15 text-indigo-300">
                      Hyper Dusk
                    </span>
                  )}
                </button>

                {project.error_message && (
                  <p className="mt-1.5 text-xs text-red-400 truncate">
                    {project.error_message}
                  </p>
                )}
              </div>

              <div className="absolute top-2 right-2 flex items-center gap-1 z-5">
                {project.status === 'completed' && project.final_video_url && (
                  <div className="relative">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (shareMenuId === project.id) {
                          setShareMenuId(null);
                          return;
                        }
                        if (!project.share_token) {
                          await resolveShareToken(project);
                        }
                        setShareMenuId(project.id);
                      }}
                      disabled={generatingTokenId === project.id}
                      className="p-1.5 text-slate-400 hover:text-blue-400 rounded-md bg-black/40 backdrop-blur-sm hover:bg-black/60 disabled:opacity-50 transition-colors"
                      title="Share video"
                    >
                      {generatingTokenId === project.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Share2 className="w-3.5 h-3.5" />
                      )}
                    </button>

                    {shareMenuId === project.id && (
                      <div
                        className="absolute right-0 top-full mt-1 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl shadow-black/60 z-50 overflow-hidden animate-fade-in"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => handleCopyShareLink(project)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-white hover:bg-slate-700 transition-colors border-b border-slate-700"
                        >
                          {copiedId === project.id ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                          {copiedId === project.id ? 'Link copied!' : 'Copy link'}
                        </button>
                        <button
                          onClick={() => handleSocialShare(project, 'whatsapp')}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-white hover:bg-slate-700 transition-colors"
                        >
                          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-slate-400" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          WhatsApp
                        </button>
                        <button
                          onClick={() => handleSocialShare(project, 'facebook')}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-white hover:bg-slate-700 transition-colors"
                        >
                          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-slate-400" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                          Facebook
                        </button>
                        <button
                          onClick={() => handleSocialShare(project, 'twitter')}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-white hover:bg-slate-700 transition-colors"
                        >
                          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-slate-400" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                          X (Twitter)
                        </button>
                        <button
                          onClick={() => handleSocialShare(project, 'email')}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-white hover:bg-slate-700 transition-colors"
                        >
                          <Mail className="w-3.5 h-3.5 text-slate-400" />
                          Email
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {project.status === 'completed' && project.final_video_url && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      void openPropertyCutWithProject(project);
                    }}
                    className="p-1.5 rounded-md bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
                    title="Send to PropertyCuts"
                  >
                    <img
                      src="/propertycut-icon.png"
                      alt="PropertyCuts"
                      className="w-3.5 h-3.5 object-contain"
                    />
                  </button>
                )}
                {project.status === 'completed' && project.final_video_url && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(
                        project.final_video_url!,
                        `ImageMotion Video - ${project.name || 'Untitled'}.mp4`,
                        project.id,
                        project.final_video_path
                      );
                    }}
                    disabled={downloadingId === project.id}
                    className="p-1.5 text-slate-400 hover:text-blue-400 rounded-md bg-black/40 backdrop-blur-sm hover:bg-black/60 disabled:opacity-50 transition-colors"
                    title="Download video"
                  >
                    {downloadingId === project.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteId(project.id);
                  }}
                  className="p-1.5 text-slate-400 hover:text-red-400 rounded-md bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
                  title="Delete project"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-center gap-1 mt-8">
          <button
            type="button"
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          {Array.from({ length: pageCount }, (_, i) => i + 1)
            .filter((n) => n === 1 || n === pageCount || Math.abs(n - currentPage) <= 1)
            .map((n, i, visible) => (
              <React.Fragment key={n}>
                {i > 0 && n - visible[i - 1] > 1 && <span className="px-1 text-slate-600">…</span>}
                <button
                  type="button"
                  onClick={() => changePage(n)}
                  className={`min-w-[2rem] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    n === currentPage ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  {n}
                </button>
              </React.Fragment>
            ))}
          <button
            type="button"
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === pageCount}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {showBrandManager && (
        <MediaBrandManager
          userId={userId}
          brands={brands}
          loading={brandsLoading}
          onClose={() => setShowBrandManager(false)}
          onLoadBrands={loadBrands}
          onCreateBrand={(input) => createBrandProfile(userId, input)}
          onUpdateBrand={(id, input) => updateBrandProfile(userId, id, input)}
          onDeleteBrand={(id) => deleteBrandProfile(userId, id)}
          onUploadLogo={(file) => uploadBrandProfileLogo(file, userId)}
          accentColor="#3b82f6"
        />
      )}
    </div>
  );
}
